import { fastCheckoutActions, homeActions, studioActions } from '@/redux/modules';
import { STUDIO_DOMAIN } from '@/config';
import logger from '@/utils/logger';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BLANK_DOCUMENT_ID, FORMATS_TO_RENDER_BY_SLUG } from '@/pages/Studio/studio.enum';
import useLocale from '../useLocale';
import { isBRServer, isMXServer } from '@/utils/Validators/validation';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { FAST_CHECKOUT_PAYMENT_METHODS } from '@/pages/FastCheckout/FastCheckout.enum.jsx';
import { useCheckout } from '../checkout';

/**
 * Hook to handle a whitelabel editor.
 *
 * @returns {{
 *   openStudio: (
 *     destination: 'template' | 'format' | 'new_document' | 'edit_document' | 'documents_history' | 'logo_builder' | 'dashboard' | 'background_remover' | 'brand_kit',
 *     id?: string
 *   ) => void,
 *  requestUserAccountData: () => void,
 *  requestTemplates: () => void,
 *  provisioningFreePlan: () => void,
 *  provisioningTrailWithoutCreditCard: (onSuccessCallback) => void,
 * checkoutTrialWithCreditCard: ({ pid, coupon, couponAvailable }) => void,
 * }}
 */
function useStudio() {
  const dispatch = useDispatch();
  const { account, product: studioAccountProduct } = useSelector(state => state.studio);
  const { loading: fastcheckoutLoading } = useSelector(state => state.fastCheckout);
  const { redirectToCheckout } = useCheckout();
  const { routes: routesLocale, fastCheckout: fastCheckoutLocale } = useLocale();

  const requestUserAccountData = useCallback(() => {
    dispatch(studioActions.account.request());
  }, [dispatch]);

  const requestTemplates = useCallback(() => {
    const [firstPart, secondPart] = [
      FORMATS_TO_RENDER_BY_SLUG.slice(0, Math.ceil(FORMATS_TO_RENDER_BY_SLUG.length / 2)),
      FORMATS_TO_RENDER_BY_SLUG.slice(Math.ceil(FORMATS_TO_RENDER_BY_SLUG.length / 2)),
    ];

    dispatch(studioActions.templates.request(firstPart));
    dispatch(studioActions.templates.request(secondPart));
  }, [dispatch]);

  const openStudio = useCallback((destination, id) => {
    if (destination === 'template' || destination === 'new_document') {
      // need to create the document before opening the editor, there is a redirect on sagas
      dispatch(studioActions.documentCreation.request({ docType: destination, id, accessToken: account.access_token }));
      return;
    }

    let url;
    switch (destination) {
      case 'format':
        url = `https://${STUDIO_DOMAIN}/redirect/format/${id}/${account.access_token}`;
        break;
      case 'edit_document':
      case 'logo_builder':
        url = `https://editor.${STUDIO_DOMAIN}/editor/${id || BLANK_DOCUMENT_ID}/${account.access_token}`;
        break;
      case 'documents_history':
        url = `https://${STUDIO_DOMAIN}/redirect/documents/${account.access_token}`;
        break;
      case 'dashboard':
        url = `https://${STUDIO_DOMAIN}/redirect/templates/${account.access_token}`;
        break;
      case 'background_remover':
        url = `https://${STUDIO_DOMAIN}/redirect/background-removal/${account.access_token}`;
        break;
      case 'brand_kit':
        url = `https://${STUDIO_DOMAIN}/redirect/brand-kit/${account.access_token}`;
        break;
      default:
        logger.error(`useStudio - method openStudio - Invalid destination: ${destination}`);
        return;
    }

    window.open(url, '_blank');
  }, [account, dispatch]);

  const provisioningFreePlan = useCallback(() => {
    dispatch(studioActions.provisionFree.request());
  }, [dispatch]);

  const provisioningTrailWithoutCreditCard = useCallback((onSuccessCallback) => {
    const getPaymentMethod = () => {
      if (isBRServer) {
        return FAST_CHECKOUT_PAYMENT_METHODS.PIX;
      }

      if (isMXServer) {
        return FAST_CHECKOUT_PAYMENT_METHODS.CUPON_DE_PAGO_MX;
      }

      return FAST_CHECKOUT_PAYMENT_METHODS.CUPON_DE_PAGO;
    };

    const productId = HgProducts.STUDIO_PRO_TRIAL;
    const productData = fastCheckoutLocale.products[productId];
    const paymentMethod = getPaymentMethod();

    const handleSuccessCallback = () => {
      requestUserAccountData();
      dispatch(homeActions.getHomeContent.reset());
      onSuccessCallback && onSuccessCallback();
    };

    dispatch(fastCheckoutActions.purchase.request({
      discount: [],
      product: [{
        serviceId: studioAccountProduct && studioAccountProduct.hosting_id,
        productId,
        billingCycle: productData.defaultCycle,
        domain: '', // FastCheckout don't have domains yet
      }],
      paymentMethod,
      onSuccessCallback: handleSuccessCallback,
    }));
  }, [dispatch, requestUserAccountData, fastCheckoutLocale.products, studioAccountProduct]);

  const checkoutTrialWithCreditCard = useCallback(({
    pid, coupon = null, couponAvailable = false,
  }) => {
    const onSuccessRoute = routesLocale.studio;

    dispatch(homeActions.getHomeContent.reset());

    redirectToCheckout({
      pid, onSuccessRoute, coupon, couponAvailable, serviceId: studioAccountProduct.hosting_id,
    });
  }, [redirectToCheckout, dispatch, routesLocale.studio, studioAccountProduct]);

  return {
    openStudio,
    requestUserAccountData,
    requestTemplates,
    provisioningFreePlan,
    provisioningTrailWithoutCreditCard,
    checkoutTrialWithCreditCard,
    fastcheckoutLoading,
  };
}

export default useStudio;
